// project.ts — CPU twin of the vertex shader's EWA projection (shaders.ts).
// The GPU path can't be unit-tested, so the exact same arithmetic lives here
// where vitest can check it against a numerically differentiated projection;
// the picker also uses it to size each gaussian's on-screen footprint.

import { blurred, lensCov, pinholeJacobian } from './lens';
import type { Lens } from './lens';
import type { Mat4 } from './math';

export interface Projected2D {
  // Screen position in device pixels, origin at the viewport centre, +y up.
  cx: number;
  cy: number;
  depth: number; // positive distance along the view axis
  a: number; // Σ'xx (incl. 0.3 px low-pass)
  b: number; // Σ'xy
  c: number; // Σ'yy (incl. 0.3 px low-pass)
  lambda1: number;
  lambda2: number;
  major: [number, number]; // 1σ axis, px
  minor: [number, number]; // 1σ axis, px
  opScale: number; // opacity scale of the lens blur (1 without one)
}

export const LOW_PASS_PX = 0.3;

// projectGaussian mirrors VERTEX_SHADER for one gaussian with world position
// p and 3D covariance cov6 = [xx,xy,xz,yy,yz,zz]. Returns null when culled
// by the near plane. With a lens, a/b/c and opScale are the blurred ones
// (the shader's u_lensOn branch).
export function projectGaussian(
  view: Mat4,
  fx: number,
  fy: number,
  p: ArrayLike<number>,
  cov6: ArrayLike<number>,
  lens?: Lens | null,
): Projected2D | null {
  const x = view[0] * p[0] + view[4] * p[1] + view[8] * p[2] + view[12];
  const y = view[1] * p[0] + view[5] * p[1] + view[9] * p[2] + view[13];
  const z = view[2] * p[0] + view[6] * p[1] + view[10] * p[2] + view[14];
  if (z > -0.01) return null;

  // Rows of the Jacobian of (u,v) = f·(x,y)/(-z), both negated.
  const j00 = fx / z, j02 = -(fx * x) / (z * z);
  const j11 = fy / z, j12 = -(fy * y) / (z * z);
  // W = upper-left 3×3 of the view matrix (row r, col k = view[k*4+r]).
  const W = (r: number, k: number): number => view[k * 4 + r];
  // M = J·W (2×3).
  const m0 = [0, 1, 2].map((k) => j00 * W(0, k) + j02 * W(2, k));
  const m1 = [0, 1, 2].map((k) => j11 * W(1, k) + j12 * W(2, k));
  const S = [
    [cov6[0], cov6[1], cov6[2]],
    [cov6[1], cov6[3], cov6[4]],
    [cov6[2], cov6[4], cov6[5]],
  ];
  const quad = (u: number[], v: number[]): number => {
    let s = 0;
    for (let i = 0; i < 3; i++) for (let k = 0; k < 3; k++) s += u[i] * S[i][k] * v[k];
    return s;
  };
  let a = quad(m0, m0) + LOW_PASS_PX;
  let b = quad(m0, m1);
  let c = quad(m1, m1) + LOW_PASS_PX;

  let opScale = 1;
  if (lens) {
    // The lens works in OpenCV camera axes and pixels (y down); this
    // covariance's y is up, so its cross term is the OpenCV one negated.
    const t = [x, -y, -z];
    const lc = lensCov(lens, fx, fy, pinholeJacobian(fx, fy, t), t);
    const r = blurred([a, -b, c], a * c - b * b, lc);
    [a, b, c] = [r.cov[0], -r.cov[1], r.cov[2]];
    opScale = r.opScale;
  }

  const mid = 0.5 * (a + c);
  const radius = Math.hypot(0.5 * (a - c), b);
  const lambda1 = mid + radius;
  const lambda2 = Math.max(mid - radius, 0.1);
  let d: [number, number];
  if (Math.abs(b) > 1e-8) {
    const n = Math.hypot(b, lambda1 - a);
    d = [b / n, (lambda1 - a) / n];
  } else {
    d = a >= c ? [1, 0] : [0, 1];
  }
  const s1 = Math.min(Math.sqrt(lambda1), 1024);
  const s2 = Math.min(Math.sqrt(lambda2), 1024);
  return {
    cx: (fx * x) / -z,
    cy: (fy * y) / -z,
    depth: -z,
    a, b, c, lambda1, lambda2,
    major: [s1 * d[0], s1 * d[1]],
    minor: [s2 * d[1], -s2 * d[0]],
    opScale,
  };
}

// focalFromProjection returns fx, fy in pixels for a GL projection matrix and
// a viewport size (fx = P00·w/2, fy = P11·h/2).
export function focalFromProjection(proj: Mat4, width: number, height: number): [number, number] {
  return [(proj[0] * width) / 2, (proj[5] * height) / 2];
}
